import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { listLeads, adminLogin, adminMe, clearToken, getToken } from "../lib/api";
import { Loader2, Inbox, ArrowLeft, Lock, LogOut } from "lucide-react";
import { Logo } from "../components/Logo";

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await adminLogin(email, password);
      toast.success("Welcome back, Admin.");
      onSuccess(user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6 pt-28">
      <div className="w-full max-w-md rounded-[2rem] border border-line bg-white/70 p-8 shadow-xl md:p-10">
        <Logo />
        <div className="mt-6 flex items-center gap-2 text-forest">
          <Lock className="h-5 w-5" />
          <h1 className="font-heading text-2xl font-bold">Admin login</h1>
        </div>
        <p className="mt-1 text-sm text-moss">Sign in to view captured leads.</p>
        <form onSubmit={submit} data-testid="admin-login-form" className="mt-8 space-y-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs uppercase tracking-[0.2em] text-moss">Email</label>
            <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@…" className="field-underline w-full text-ink placeholder:text-ink/30" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs uppercase tracking-[0.2em] text-moss">Password</label>
            <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" className="field-underline w-full text-ink placeholder:text-ink/30" />
          </div>
          <button data-testid="admin-login-btn" type="submit" disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-forest py-4 font-semibold text-cream transition-transform duration-300 hover:scale-[0.98] hover:bg-forest-hover disabled:opacity-60">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
          </button>
        </form>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-moss hover:text-forest">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    adminMe().then(() => setAuthed(true)).catch(() => clearToken()).finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!authed) return;
    setLoading(true);
    listLeads().then(setLeads).catch(() => clearToken() || setAuthed(false)).finally(() => setLoading(false));
  }, [authed]);

  const logout = () => { clearToken(); setAuthed(false); };

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-cream"><Loader2 className="h-8 w-8 animate-spin text-forest" /></div>;
  if (!authed) return <LoginForm onSuccess={() => setAuthed(true)} />;

  return (
    <main data-testid="admin-page" className="min-h-screen bg-cream pt-28">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-moss hover:text-forest">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <div className="mb-8 flex items-end justify-between">
          <div>
            <Logo />
            <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-forest">Leads dashboard</h1>
            <p className="mt-1 text-moss">{leads.length} enquir{leads.length === 1 ? "y" : "ies"} captured</p>
          </div>
          <button data-testid="admin-logout" onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-forest transition-colors hover:bg-sand">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-forest" /></div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-3xl border border-line bg-white/50 py-24 text-moss">
            <Inbox className="h-10 w-10" /> No leads yet.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-3xl border border-line bg-white/60">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-line bg-sand font-mono text-xs uppercase tracking-widest text-moss">
                <tr>
                  {["Name", "Phone", "Email", "City", "Type", "Bill", "Source", "Date"].map((h) => (
                    <th key={h} className="px-4 py-4 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="leads-table">
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-line/60 last:border-0 hover:bg-sand/50">
                    <td className="px-4 py-3 font-semibold text-forest">{l.name}</td>
                    <td className="px-4 py-3">{l.phone}</td>
                    <td className="px-4 py-3">{l.email}</td>
                    <td className="px-4 py-3">{l.city || "-"}</td>
                    <td className="px-4 py-3">{l.property_type}</td>
                    <td className="px-4 py-3">{l.monthly_bill || "-"}</td>
                    <td className="px-4 py-3 text-xs text-moss">{l.source_page}</td>
                    <td className="px-4 py-3 font-mono text-xs text-moss">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
